/**
 * MV2-1802 — situação de pagamento de uma recarga: paga, pendente ou sem cobrança.
 *
 * A mesma pergunta era respondida por quatro implementações diferentes — o filtro PAID/UNPAID do CS
 * e três lugares aqui — e elas discordavam entre si. O caso que abriu isto: a recarga 83627 de test1
 * aparecia PAGA no app e PENDENTE no dashboard. Pagamento tem duas pernas, a cobrança no motorista
 * (`status_b_to_c`) e o repasse ao lojista (`status_b_to_b`), e cada tela olhava uma.
 *
 * Quem decide agora é o CS, no campo `payment_situation` da listagem de recargas — calculado pelas
 * mesmas condições que o filtro usa (`paymentIsPaid`/`paymentIsUnpaid`/`paymentIsPriceless` em
 * `TransactionRepositoryImpl`). O cálculo local abaixo é só a ponte para quando o dashboard sobe
 * antes do CS; some quando o campo estiver em produção.
 */

export enum PaymentSituation {
  PAID = 'PAID',
  UNPAID = 'UNPAID',
  PRICELESS = 'PRICELESS',
}

interface PaymentSituationInput {
  paymentSituation?: string;
  hasPayment?: boolean;
  income?: number;
  paidTimestamp?: string | null;
  paidError?: string | null;
  paymentStatus?: string | null;
  paymentStatusDetail?: string | null;
  statusBtoB?: string | null;
}

/**
 * O repasse ao lojista não reprovou. Nulo significa "este pagamento não tem repasse a fazer" — é o
 * normal de tenant sem split configurado, que nunca recebe esse campo — e não "repasse pendente".
 */
export const isTransferSettled = (statusBtoB?: string | null): boolean =>
  statusBtoB == null || statusBtoB === 'ACCREDITED';

const isChargeSettled = (transaction: PaymentSituationInput): boolean =>
  transaction?.paidTimestamp != null && transaction?.paidError === 'NoError';

const isCancelledByMinEnergyLimit = (
  transaction: PaymentSituationInput,
): boolean =>
  transaction?.paymentStatus === 'cancelled' &&
  transaction?.paymentStatusDetail === 'cancelled_energy_limit_min';

export const getPaymentSituation = (
  transaction: PaymentSituationInput,
): PaymentSituation => {
  // O CS já decidiu: não recalcula.
  if (
    transaction?.paymentSituation != null &&
    transaction.paymentSituation in PaymentSituation
  ) {
    return transaction.paymentSituation as PaymentSituation;
  }

  if (!transaction?.hasPayment || !(transaction?.income > 0)) {
    return PaymentSituation.PRICELESS;
  }

  if (!isTransferSettled(transaction?.statusBtoB)) {
    return PaymentSituation.UNPAID;
  }

  if (!isChargeSettled(transaction)) {
    return PaymentSituation.UNPAID;
  }

  if (isCancelledByMinEnergyLimit(transaction)) {
    return PaymentSituation.PRICELESS;
  }

  if (
    transaction?.paymentStatus == null ||
    transaction.paymentStatus === 'approved' ||
    transaction.paymentStatus === 'authorized'
  ) {
    return PaymentSituation.PAID;
  }

  return PaymentSituation.UNPAID;
};
